import {useCallback, useEffect} from 'react';
import {getCurrentUrlWithAdditonalParam} from '../../../../../../common/getCurrentUrlWithAdditonalParam';
import {tt} from '../../../../../../common/getTranslations';
import {requestContext} from '../../../../../../common/requestContext';
import {TRIGGER_COMMAND_KEY} from '../../../../../../common/shared/constants';
import {
  COMMAND_KEY,
  useCommandWithLocalStorage,
} from '../../../../../../common/shared/hooks/useCommandWithLocalStorage';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import {commonErrorHandler} from '../../../../../../errors/commonErrorHandler';
import {ResultStatus} from '../../../../../../graphql/generated/types';
import {showToast} from '../../../../../../toast/toastHandler';
import {useLogin} from '../../../../../../user/useLogin.hook';
import {useViewerEndQuery} from '../../../../../../wrapper/viewerWrapper';
import {useSetSeriesMutationHandler} from '../../../../../inven/[invenType]/hook/useSetSeriesMutationHandler';
import {generateRelayRedirectUrl} from '../../../../../relay/redirect/util/generateRelayRedirectUrl';
import {VIEWER_TOAST_MESSAGES} from '../constant/viewerConstant';
import {updateCacheIsAlarmOn} from '../util/updateCacheIsAlarmOn';
import {updateCacheIsLikeOn} from '../util/updateCacheIsLikeOn';

export function useSeriesLikeHook({
  seriesId,
  productId,
}: {
  seriesId: number;
  productId: number;
}) {
  const dispatch = useTypedDispatch();
  const {isLogin, doLogin} = useLogin();
  const {isWebView} = requestContext.requestInfo.display;
  const {saveCommand, runCommand} = useCommandWithLocalStorage();
  const {onSetSeriesLikeMutate} = useSetSeriesMutationHandler();

  const viewerEndResult = useViewerEndQuery({seriesId, productId});
  const isLikeSeries = viewerEndResult.data?.likeOn ?? false;
  const viewerEndData = viewerEndResult.data;

  const onClickLikeSeries = useCallback(
    (parameters?: {fromSavedCommand?: boolean}) => {
      if (!isLogin) {
        if (!parameters?.fromSavedCommand) {
          saveCommand({
            commandKey: COMMAND_KEY.CLICK_LIKE,
            command: {location: window.location.pathname},
          }).then(() =>
            doLogin(
              generateRelayRedirectUrl(
                getCurrentUrlWithAdditonalParam(
                  TRIGGER_COMMAND_KEY,
                  COMMAND_KEY.CLICK_LIKE
                )
              )
            )
          );
        }
        return;
      }
      if (!seriesId || !productId) {
        return;
      }
      if (isWebView) {
        window?.KPApp?.onClickLike?.();
        return;
      }
      onSetSeriesLikeMutate(seriesId, !isLikeSeries)
        .then(result => {
          const message = isLikeSeries
            ? VIEWER_TOAST_MESSAGES.likeOff
            : VIEWER_TOAST_MESSAGES.likeOn;

          if (result.setSeriesLike.status !== ResultStatus.Success) {
            return;
          }
          updateCacheIsAlarmOn({seriesId, productId, isAlarmOn: !isLikeSeries});
          updateCacheIsLikeOn({seriesId, productId, isLikeOn: !isLikeSeries});
          showToast({
            message: tt(message),
            isMobile: requestContext.requestInfo.display.isMobile,
          });
        })
        .catch(error => {
          console.log('error', error);
          commonErrorHandler(error);
        });
    },
    [
      isLogin,
      seriesId,
      productId,
      isWebView,
      onSetSeriesLikeMutate,
      isLikeSeries,
      saveCommand,
      doLogin,
      dispatch,
    ]
  );

  useEffect(() => {
    if (viewerEndData) {
      runCommand({
        commandKey: COMMAND_KEY.CLICK_LIKE,
        action: async () => {
          if (isLikeSeries) {
            showToast({
              message: tt(VIEWER_TOAST_MESSAGES.likeAlready),
              isMobile: requestContext.requestInfo.display.isMobile,
            });
          } else {
            await onClickLikeSeries({fromSavedCommand: true});
          }
        },
      });
    }
  }, [isLikeSeries, onClickLikeSeries, runCommand, viewerEndData]);
  return {onClickLikeSeries};
}
